import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AtiraEmptyWithAction } from '../../../components/AtiraEmptyWithAction';
import { AtiraSelect } from '../../../components/AtiraSelect';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { AtiraLinkLang } from '../../../model/settings/types/AtiraLinkLang.enum';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { AtiraLinkActions } from '../../../redux/atira-link/atira-link.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { DefaultOptionType } from '../../../utils/local-antd';
import { AtiraLinkPreviewPhoneWithContext } from '../../my-link/components/AtiraLinkPreviewPhoneWithContext';

const StyledSection = styled(Section)`
  width: 100%;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 80%;
  }
`;

const StyledButton = styled(Button)`
  height: 2.3rem;
  width: 10rem;
  margin-top: ${Spacing.l};
`;

const PreviewPhoneWrapper = styled(Flex)`
  display: none;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    display: flex;
  }
`;

const languageOptions = (t: TFunction) =>
  [
    {
      label: t('common.arabic'),
      value: AtiraLinkLang.AR,
      'data-testid': atiraLinkTestIDs.settings.language.lang(AtiraLinkLang.AR),
    },
    {
      label: t('common.english'),
      value: AtiraLinkLang.EN,
      'data-testid': atiraLinkTestIDs.settings.language.lang(AtiraLinkLang.EN),
    },
  ] as DefaultOptionType[];

export const SettingsLanguage: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const formMethods = useForm<EditAtiraLinkDto>();

  const { getValues, handleSubmit, control, watch, reset } = formMethods;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const userAtiraLinks = useAppSelector(
    atiraLinkSliceSelectors.selectUserAtiraLinks,
  );

  const selectedAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const currentLinkLang = watch('settings.lang.current');

  const onUpdate = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      if (
        dto.settings?.lang?.current !==
        selectedAtiraLink?.settings?.lang?.current
      ) {
        await dispatch(AtiraLinkActions.editAtiraLink(dto)).unwrap();
        await dispatch(AtiraLinkActions.getUserAtiraLinks({ userId })).unwrap();
      }

      AtiraToast.success(t('settings.language.update.success'));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reset({
      atiraLinkId: selectedAtiraLink?._id,
      userId: userId,
      settings: {
        lang: {
          current:
            selectedAtiraLink?.settings?.lang?.current || AtiraLinkLang.EN,
        },
      },
    });
  }, [reset, selectedAtiraLink, userId]);

  if (!userAtiraLinks.length) {
    return (
      <AtiraEmptyWithAction
        description={t('settings.no_link')}
        buttonTitle={t('common.create_link')}
        onClick={() => navigate('/my-link')}
      />
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Flex justifyContent="space-between" gap="m">
        <StyledSection title={t('common.language')}>
          <Text color="fgSecondary">{t('settings.language.description')}</Text>

          <Controller
            control={control}
            name="settings.lang.current"
            render={({ field: { value, onChange } }) => (
              <AtiraSelect
                value={value}
                onChange={onChange}
                options={languageOptions(t)}
                title={t('common.language')}
                containerStyle={{ width: '15rem' }}
                dataTestId={
                  atiraLinkTestIDs.settings.language.dropdownContainer
                }
              />
            )}
          />

          <StyledButton
            loading={loading}
            onClick={handleSubmit(onUpdate)}
            dataTestId={atiraLinkTestIDs.settings.language.submit}
          >
            {t('common.update')}
          </StyledButton>
        </StyledSection>

        <PreviewPhoneWrapper>
          <AtiraLinkPreviewPhoneWithContext
            currentLinkLang={currentLinkLang}
            values={selectedAtiraLink!}
            isPreviewMode
          />
        </PreviewPhoneWrapper>
      </Flex>
    </FormProvider>
  );
};
